import React from 'react';
import { RotateCcw } from 'lucide-react';
import {
	ResourceCategoryLabels,
	ResourceParentCategoryLabels,
	ResourceStatus,
} from '../../../shared/models/Resource';
import { DEFAULT_FILTERS } from './constants/resource.constants';
import { formatStatusLabel } from './utils/resourceHelpers';
import { FilterState } from './types/resource.types';

export interface ResourceFilterOptions {
	parentCategories: string[];
	categories: string[];
	authors: string[];
	years: string[];
}

export interface ResourceFiltersProps {
	filters: FilterState;
	filterOptions: ResourceFilterOptions;
	onFilterChange: <K extends keyof FilterState>(
		key: K,
		value: FilterState[K]
	) => void;
	hasActiveFilters: boolean;
	disabled?: boolean;
}

interface SelectOption {
	value: string;
	label: string;
}

interface FilterSelectProps {
	id: string;
	label: string;
	value: string;
	options: SelectOption[];
	onChange: (value: string) => void;
	disabled?: boolean;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
	id,
	label,
	value,
	options,
	onChange,
	disabled,
}) => (
	<div className="resource-actions">
		<label htmlFor={id}>{label}</label>
		<select
			id={id}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			disabled={disabled}
		>
			{options.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
	</div>
);

export const ResourceFilters: React.FC<ResourceFiltersProps> = ({
	filters,
	filterOptions,
	onFilterChange,
	hasActiveFilters,
	disabled = false,
}) => {
	const statusOptions: SelectOption[] = [
		{ value: DEFAULT_FILTERS.status, label: 'All Statuses' },
		{ value: ResourceStatus.DRAFT, label: formatStatusLabel(ResourceStatus.DRAFT) },
		{ value: ResourceStatus.PUBLISHED, label: formatStatusLabel(ResourceStatus.PUBLISHED) },
		{ value: ResourceStatus.ARCHIVED, label: formatStatusLabel(ResourceStatus.ARCHIVED) },
		{ value: ResourceStatus.UNDER_REVIEW, label: formatStatusLabel(ResourceStatus.UNDER_REVIEW) },
	];

	const parentCategoryOptions: SelectOption[] = [
		{ value: DEFAULT_FILTERS.parentCategory, label: 'All Parent Categories' },
		...filterOptions.parentCategories.map((value) => ({
			value,
			label: ResourceParentCategoryLabels[value] || value,
		})),
	];

	const categoryOptions: SelectOption[] = [
		{ value: DEFAULT_FILTERS.category, label: 'All Categories' },
		...filterOptions.categories.map((value) => ({
			value,
			label: ResourceCategoryLabels[value as keyof typeof ResourceCategoryLabels] || value,
		})),
	];

	const authorOptions: SelectOption[] = [
		{ value: DEFAULT_FILTERS.author, label: 'All Authors' },
		...filterOptions.authors.map((value) => ({ value, label: value })),
	];

	const yearOptions: SelectOption[] = [
		{ value: DEFAULT_FILTERS.year, label: 'All Years' },
		...filterOptions.years.map((value) => ({ value, label: value })),
	];

	const handleClearFilters = () => {
		onFilterChange('status', DEFAULT_FILTERS.status);
		onFilterChange('parentCategory', DEFAULT_FILTERS.parentCategory);
		onFilterChange('category', DEFAULT_FILTERS.category);
		onFilterChange('featured', DEFAULT_FILTERS.featured);
		onFilterChange('author', DEFAULT_FILTERS.author);
		onFilterChange('year', DEFAULT_FILTERS.year);
	};

	return (
		<div className="filter-bar resource-filters" aria-label="Resource filters">
			<FilterSelect
				id="resource-filter-status"
				label="Status"
				value={filters.status}
				options={statusOptions}
				onChange={(value) => onFilterChange('status', value as FilterState['status'])}
				disabled={disabled}
			/>

			<FilterSelect
				id="resource-filter-parent-category"
				label="Parent Category"
				value={filters.parentCategory}
				options={parentCategoryOptions}
				onChange={(value) => onFilterChange('parentCategory', value)}
				disabled={disabled}
			/>

			<FilterSelect
				id="resource-filter-category"
				label="Category"
				value={filters.category}
				options={categoryOptions}
				onChange={(value) => onFilterChange('category', value as FilterState['category'])}
				disabled={disabled}
			/>

			<FilterSelect
				id="resource-filter-featured"
				label="Featured"
				value={filters.featured}
				options={[
					{ value: DEFAULT_FILTERS.featured, label: 'All Featured States' },
					{ value: 'true', label: 'Featured' },
					{ value: 'false', label: 'Not Featured' },
				]}
				onChange={(value) => onFilterChange('featured', value as FilterState['featured'])}
				disabled={disabled}
			/>

			<FilterSelect
				id="resource-filter-author"
				label="Author"
				value={filters.author}
				options={authorOptions}
				onChange={(value) => onFilterChange('author', value)}
				disabled={disabled}
			/>

			<FilterSelect
				id="resource-filter-year"
				label="Year"
				value={filters.year}
				options={yearOptions}
				onChange={(value) => onFilterChange('year', value)}
				disabled={disabled}
			/>

			<div className="resource-actions">
				<label htmlFor="resource-filter-clear">Actions</label>
				<button
					id="resource-filter-clear"
					type="button"
					className="btn-secondary"
					onClick={handleClearFilters}
					disabled={disabled || !hasActiveFilters}
				>
					<RotateCcw size={14} />
					<span>Clear Filters</span>
				</button>
			</div>
		</div>
	);
};
